import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DynamicQuestionnaire } from "../DynamicQuestionnaire";
import { selectSelectedAssistanceProgramById } from "../../../../../store/selector";

/**
 * Native JSON field type `eligibilityQuestionnaire`: a first-class concept that renders the dynamic
 * eligibility questionnaire from the program's questionnaire config. DynamicQuestionnaire reads the
 * config (expressEnrollmentConfiguration.expressEnrollmentQuestionnaire) + applicationForm internally
 * and registers the answers under eligibilityQuestionnaire.<linkId>, so this wrapper only supplies the
 * display strings — the divider text and the ineligible-answer messages (which interpolate the
 * program support phone) — via dedicatedFieldProps. No props need to come from the modal.
 *
 * FormJson usage:
 *   { "type": "eligibilityQuestionnaire", "name": "eligibilityQuestionnaire", "colSize": 24,
 *     "dedicatedFieldProps": { "dividerText": "<i18n key>", "ineligibleAnswerErrorKey": "<i18n key>",
 *       "ineligibleCheckboxAnswerErrorKey": "<i18n key>", "sectionId": "<anchor id>",
 *       "phoneField": "phone2" } }
 */
export const EligibilityQuestionnaireField = ({
  dividerText,
  ineligibleAnswerErrorKey,
  ineligibleCheckboxAnswerErrorKey,
  sectionId,
  phoneField
}) => {
  const { t } = useTranslation();
  const assistanceProgram = useSelector(selectSelectedAssistanceProgramById);
  // The support phone interpolated into the ineligible-answer messages is brand-specific (pfizer-iv
  // uses phone2, Genentech copay uses phone1), so the source field is declared per-form via
  // dedicatedFieldProps.phoneField. When unset, fall back to the app-wide support-phone resolution.
  const phone_number = phoneField
    ? assistanceProgram?.[phoneField]
    : assistanceProgram?.helpdeskPhone ||
      assistanceProgram?.phone1 ||
      assistanceProgram?.phone2 ||
      assistanceProgram?.fax;

  return (
    <DynamicQuestionnaire
      DividerText={dividerText}
      ineligibleAnswerError={ineligibleAnswerErrorKey ? t(ineligibleAnswerErrorKey, { phone_number }) : undefined}
      ineligibleCheckboxAnswerError={
        ineligibleCheckboxAnswerErrorKey ? t(ineligibleCheckboxAnswerErrorKey, { phone_number }) : undefined
      }
      dynamicQuestionnaireSectionId={sectionId}
    />
  );
};

// This native field renders its OWN section chrome (DividerText + anchor id via DynamicQuestionnaire),
// so the JSON renderer skips the SectionPart wrapper for any section containing it — avoiding an empty
// divider and a duplicate `enrollment-form-*` id. The renderer derives the decision from this flag.
EligibilityQuestionnaireField.rendersOwnSection = true;

export default EligibilityQuestionnaireField;
